import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import {
	generateTrackingId
} from "client/Misc/utils";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import {
	CollapsibleList,
	HorizontalAlignment,
	CollapsibleListItemContextData,
	CollapsibleListItemContext,
	CollapsibleListChildWithKeyAndIndex,
	CollapsibleListItemRenderMode,
	VerticalAlignment
} from "../CollapsibleList/CollapsibleList";
import { localize } from "client_legacy/ndaw/culture";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks/useKeyboardListNavigation";
import { Key } from "client/Misc/commonTypes";
import { flattenChildren } from "client/Misc/reactUtils";

export type DropDownDirection = "down" | "up";

export type CollapsibleMenuProps = {
	horizontalAlignment?: HorizontalAlignment;
	verticalAlignment?: VerticalAlignment;
	dropDownDirection?: DropDownDirection;
	moreButtonFactory: (
		isActive: boolean,
		tabIndex: number,
		onIsActiveChange: (newValue: boolean) => void,
		onKeyDown: (event: KeyboardEvent<HTMLElement>) => void,
		ref: (element: HTMLElement | null) => void
	) => ReactElement;
	shouldOnlyMeasure?: boolean;
	onMaxContentWidthChange?(maxContentWidth: number): void;
	trackingId?: string;
	shouldPushMoreButtonRight?: boolean;
	height?: "auto" | "fill";
};

export const CollapsibleMenu = (props: PropsWithChildren<CollapsibleMenuProps>): ReactElement => {
	const {
		horizontalAlignment = "left",
		verticalAlignment = "top",
		dropDownDirection = "down",
		moreButtonFactory,
		trackingId,
		shouldOnlyMeasure = false,
		onMaxContentWidthChange,
		shouldPushMoreButtonRight = false,
		height = "auto",
		children
	} = props;

	const moreButtonWrapperRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	const [isMoreButtonActive, setIsMoreButtonActive] = useState<boolean>(false);
	const [invisibleNodes, setInvisibleNodes] = useState<CollapsibleListChildWithKeyAndIndex[]>([]);
	const [tabbableIndex, setTabbableIndex] = useState<number>(0);

	const nodesToRenderInDropDown = useMemo(
		(): CollapsibleListChildWithKeyAndIndex[] => {
			return invisibleNodes.filter(n => n.shouldRenderInCollapsedMode);
		},
		[invisibleNodes]
	);

	const {
		contextData: collapsibleListKeyboardNavigationContext
	} = useKeyboardListNavigation({
		navigationDirection: "horizontal",
		onItemFocusChange: setTabbableIndex
	});

	const openDropOverHandler = useCallback(
		(): void => {
			setIsMoreButtonActive(true);
		},
		[]
	);

	const closeDropOverHandler = useCallback(
		(): void => {
			setIsMoreButtonActive(false);
		},
		[]
	);

	const moreButtonIndex = useMemo(
		(): number => {
			return flattenChildren(children).length;
		},
		[children]
	);

	return (
		<>
			<KeyBoardListNavigationContext.Provider value={collapsibleListKeyboardNavigationContext}>
				<CollapsibleList
					moreButton={
						(): ReactElement => (
							<MoreButton
								index={moreButtonIndex}
								tabIndex={tabbableIndex === moreButtonIndex ? 0 : -1}
								isActive={isMoreButtonActive}
								onIsActiveChange={setIsMoreButtonActive}
								moreButtonFactory={moreButtonFactory}
								onOpenDropOver={openDropOverHandler}
								onCloseDropOver={closeDropOverHandler}
							/>
						)
					}
					moreButtonWrapperRef={moreButtonWrapperRef}
					onInvisibleNodesChanged={setInvisibleNodes}
					shouldOnlyMeasure={shouldOnlyMeasure}
					onMaxContentWidthChange={onMaxContentWidthChange}
					horizontalAlignment={horizontalAlignment}
					verticalAlignment={verticalAlignment}
					shouldPushMoreButtonRight={shouldPushMoreButtonRight}
					trackingId={trackingId || "collapsible-menu"}
					height={height}
					tabbableIndex={tabbableIndex}
				>
					{children}
				</CollapsibleList>
			</KeyBoardListNavigationContext.Provider>
			{(isMoreButtonActive && invisibleNodes.length > 0 && moreButtonWrapperRef.current) && (
				<DropOverActionList
					target={moreButtonWrapperRef.current}
					onClose={() => setIsMoreButtonActive(false)}
					contentAlignment={
						dropDownDirection === "down"
							? "tr"
							: "br"
					}
					targetAlignment={
						dropDownDirection === "down"
							? "br"
							: "tr"
					}
					trackingId={generateTrackingId(trackingId, "__drop-over")}
					menuTitle={localize("_More")}
				>
					{nodesToRenderInDropDown.map((x, index) => (
						<CollapsedItem
							key={x.key}
							index={index}
						>
							{x.child}
						</CollapsedItem>
					))}
				</DropOverActionList>
			)}
		</>
	);
};
CollapsibleMenu.displayName = "CollapsibleMenu";

type CollapsedItemProps = {
	index: number;
};

const CollapsedItem = (props: PropsWithChildren<CollapsedItemProps>): ReactElement => {
	const {
		children,
		index
	} = props;

	const contextData = useMemo(
		(): CollapsibleListItemContextData => {
			const noop = (): void => { /* NOOP callbacks because all measurements are done with the normal items (or their placeholders) */};
			return {
				tabIndex: -1,
				renderMode: CollapsibleListItemRenderMode.Collapsed,
				index,
				onPriorityChange: noop,
				onResponsiveBehaviorChange: noop,
				onIsSpaceBetweenChange: noop,
				resetIsSpaceBetween: noop,
				resetPriority: noop,
				resetResponsiveBehavior: noop
			};
		},
		[index]
	);

	return (
		<CollapsibleListItemContext.Provider
			value={contextData}
		>
			{children}
		</CollapsibleListItemContext.Provider>
	);
};

CollapsedItem.displayName = "CollapsedItem";

type MoreButtonProps = {
	isActive: boolean;
	index: number;
	tabIndex: number;
	onOpenDropOver: () => void;
	onCloseDropOver: () => void;
	onIsActiveChange: (newValue: boolean) => void;
	moreButtonFactory: (
		isActive: boolean,
		tabIndex: number,
		onIsActiveChange: (newValue: boolean) => void,
		onKeyDown: (event: KeyboardEvent<HTMLElement>) => void,
		ref: (element: HTMLElement | null) => void
	) => ReactElement;
};

const MoreButton = (
	props: MoreButtonProps
): ReactElement => {
	const {
		isActive,
		index,
		tabIndex,
		onOpenDropOver,
		onCloseDropOver,
		onIsActiveChange,
		moreButtonFactory,
	} = props;

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		shouldCancelOnTab: true,
		shouldCancelAfterExecute: true
	});

	const keyDownHandler = useCallback(
		(event: KeyboardEvent<HTMLElement>): void => {
			switch (event.key) {
				case Key.ArrowDown:
				case Key.Enter:
				case Key.Space:
					onOpenDropOver();

					event.preventDefault();
					break;
				case Key.Escape:
				case Key.ArrowUp:
					onCloseDropOver();
					event.preventDefault();
					break;
				case Key.Tab:
					if (isActive) {
						onCloseDropOver();
						event.preventDefault();
					}
					break;
				case Key.ArrowLeft:
				case Key.ArrowRight:
					onCloseDropOver();
					onKeyDown(event);
					break;
				default:
					onKeyDown(event);
			}

		},
		[
			onCloseDropOver,
			onKeyDown,
			onOpenDropOver,
			isActive
		]
	);

	return moreButtonFactory(
		isActive,
		tabIndex,
		onIsActiveChange,
		keyDownHandler,
		ref
	);
};
MoreButton.displayName = "MoreButton";